// Project-card copy rules for 100% Commission projects (ID-118). Pure: the card maps the result through t().
import type { ExclusiveBlock } from '~/types/exclusives';

export type ExclusiveUnitsLine =
  | { key: 'exclusives.card.unitsNone' }
  | { key: 'exclusives.card.unitsOne' }
  | { key: 'exclusives.card.unitsMany'; count: number };

export type ExclusiveCardCopy = {
  units: ExclusiveUnitsLine;
  isNew: boolean;
  // Verified viewers with units, a commission % and an earnings figure; payoutSlaHours null = no FastPay.
  earnings: { amountAed: number; payoutSlaHours: number | null } | null;
  // Signed-in, phone not verified: the commission access gate replaces the earnings line.
  showCommissionGate: boolean;
};

type ActiveBlock = Extract<ExclusiveBlock, { isExclusive: true }>;

const DEFAULT_PAYOUT_SLA_HOURS = 48;

const aedFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Verified blocks always carry the terms keys (values may be null when the commission % is missing).
export const isVerifiedExclusiveBlock = (block: ActiveBlock): boolean =>
  block.payoutSlaHours !== undefined || block.agentSharePercent !== undefined;

export const unitsLineFor = (unitsAvailable: number): ExclusiveUnitsLine => {
  const count = Math.max(0, Math.floor(Number(unitsAvailable) || 0));
  if (count === 0) return { key: 'exclusives.card.unitsNone' };
  if (count === 1) return { key: 'exclusives.card.unitsOne' };
  return { key: 'exclusives.card.unitsMany', count };
};

export const exclusiveCardCopy = (block: ExclusiveBlock | null | undefined): ExclusiveCardCopy | null => {
  if (!block?.isExclusive) return null;
  const hasUnits = block.unitsAvailable > 0;
  const verified = isVerifiedExclusiveBlock(block);
  const amount = block.fromAgentEarningsAed;
  const showEarnings =
    verified && hasUnits && block.agentCommissionPercent != null && typeof amount === 'number' && amount > 0;

  return {
    units: unitsLineFor(block.unitsAvailable),
    isNew: block.isNew === true,
    earnings: showEarnings
      ? {
          amountAed: amount,
          payoutSlaHours: block.fastPayAvailable === false ? null : (block.payoutSlaHours ?? DEFAULT_PAYOUT_SLA_HOURS),
        }
      : null,
    showCommissionGate: !verified && hasUnits,
  };
};

// "AED 60,000", or "AED 60,000 (≈ $16,340)" when the viewer prefers another currency. AED always first.
export const formatExclusiveEarnings = (
  amountAed: number,
  preferred?: { currency: string; amount: number } | null,
): string => {
  const aed = `AED ${aedFormatter.format(amountAed)}`;
  if (!preferred || preferred.currency === 'AED' || !Number.isFinite(preferred.amount)) return aed;
  let converted: string;
  try {
    converted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: preferred.currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(preferred.amount);
  } catch {
    converted = `${aedFormatter.format(preferred.amount)} ${preferred.currency}`;
  }
  return `${aed} (≈ ${converted})`;
};
